/**
 * Uploads go to the haat-hub careers public endpoints (same approach as
 * `Haat_careers/Frontend_User` and PartnerOS): POST multipart/form-data to
 * `{REACT_APP_UPLOAD_API_URL}<endpoint>` and store only the returned Azure Blob
 * URL on the client.
 *
 * Endpoints (NestJS, @Public):
 *   - POST upload            for image/* (stored in the Azure images container)
 *   - POST upload-document   for application/pdf, doc, docx (Azure documents container)
 *
 * Response shape from the backend is `{ message, filePath }` but we also
 * accept `{ url }` and `[{ url }]` defensively.
 */

import { resolveLanUrl } from './resolveLanUrl';
import { getRuntimeEnv } from './runtimeConfig';

const DEFAULT_UPLOAD_BASE = 'https://hub-backend.haat.delivery/api/v1/careers/public/';

export interface UploadResult {
  url: string;
}

const normalizeUploadPayload = (json: unknown): UploadResult | null => {
  if (Array.isArray(json) && json.length > 0) {
    const first = json[0] as { url?: unknown };
    if (typeof first?.url === 'string') return { url: first.url };
  }

  const obj = json as { filePath?: unknown; url?: unknown } | null;
  if (typeof obj?.filePath === 'string') return { url: obj.filePath };
  if (typeof obj?.url === 'string') return { url: obj.url };

  return null;
};

const resolveBase = (): string => {
  const raw = resolveLanUrl(getRuntimeEnv('REACT_APP_UPLOAD_API_URL') || DEFAULT_UPLOAD_BASE);
  return raw.endsWith('/') ? raw : `${raw}/`;
};

const pickEndpoint = (file: File): 'upload' | 'upload-document' => {
  const mime = (file.type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'upload'; // careers/public/upload -> Azure images
  return 'upload-document'; // careers/public/upload-document -> Azure documents
};

export const uploadFile = async (file: File): Promise<UploadResult> => {
  const url = `${resolveBase()}${pickEndpoint(file)}`;

  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(url, { method: 'POST', body: formData });

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }

  if (!res.ok) {
    const message =
      (json as { message?: string } | null)?.message ||
      `Upload failed (${res.status})`;
    throw new Error(message);
  }

  const normalized = normalizeUploadPayload(json);
  if (!normalized?.url) {
    throw new Error('Invalid upload response: missing file URL');
  }
  return normalized;
};
